// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file AssessmentFreeResponse — free-response textarea for an assessment question
 * @version 0.2.0
 */

import React from 'react';
import type { CSSProperties } from 'react';
import styles from './AssessmentFreeResponse.module.css';

/*
 * AssessmentFreeResponse — free-response textarea for assessment screens.
 * A tall multi-line input with a live character counter beneath it, styled
 * for the dark assessment surface. The counter reports the current length
 * against the maximum ("0/800"); input is hard-capped at `maxLength` so the
 * count never exceeds the limit.
 *
 *   <AssessmentFreeResponse
 *     aria-label="Your response"
 *     maxLength={800}
 *     value={answer}
 *     onChange={setAnswer}
 *   />
 *
 * Works controlled (`value` + `onChange`) or uncontrolled (`defaultValue`).
 * Supply an accessible name via `aria-label` or `aria-labelledby`.
 */

export interface AssessmentFreeResponseProps {
  /** Controlled text value. Pair with `onChange`. */
  value?: string;
  /** Initial value for uncontrolled usage. Ignored when `value` is provided. */
  defaultValue?: string;
  /** Fires with the new (already length-capped) text on each edit. */
  onChange?: (value: string) => void;
  /** Maximum character count. Drives the counter denominator and hard-caps input. */
  maxLength?: number;
  /** Placeholder shown while empty. */
  placeholder?: string;
  /** Disables the field. */
  disabled?: boolean;
  /** Forwarded to the underlying `<textarea>`. */
  id?: string;
  /** Forwarded to the underlying `<textarea>`. */
  name?: string;
  /** Accessible name when there is no visible associated label. */
  'aria-label'?: string;
  /** ID(s) of the element(s) labelling this field. */
  'aria-labelledby'?: string;
  /** Forwarded to the root `<div>`. */
  className?: string;
  /** Forwarded to the root `<div>`. */
  style?: CSSProperties;
  /**
   * Colour treatment. `dark` (default) is the grey-border field with white ink
   * on the dark surface; `light` is a dark-blue-border field with a faint fill
   * and dark ink for the light-surface flow. A per-instance variant.
   */
  theme?: 'light' | 'dark';
}

export const AssessmentFreeResponse = React.forwardRef<
  HTMLTextAreaElement,
  AssessmentFreeResponseProps
>(function AssessmentFreeResponse(
  {
    value,
    defaultValue,
    onChange,
    maxLength = 800,
    placeholder,
    disabled,
    id,
    name,
    className,
    style,
    theme = 'dark',
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
  },
  ref,
) {
  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState<string>(defaultValue ?? '');
  const current = isControlled ? value : internal;

  const reactId = React.useId();
  const counterId = `${id ?? reactId}-counter`;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Hard-cap: never let the value exceed maxLength (native maxLength plus a
    // slice guard for programmatic / paste edge cases).
    const next = e.target.value.slice(0, maxLength);
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };

  const rootClass = [styles.root, theme === 'light' ? styles.light : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} style={style}>
      <textarea
        ref={ref}
        className={styles.field}
        id={id}
        name={name}
        value={current}
        onChange={handleChange}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={counterId}
      />
      <div className={styles.counter} id={counterId}>
        {current.length}/{maxLength}
      </div>
    </div>
  );
});

AssessmentFreeResponse.displayName = 'AssessmentFreeResponse';
